import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { deletePiaLesson, fetchPiaLessons } from '../../services/feedbackTriageApi';
import { RADIUS } from './triageTokens';

const QUERY_KEY = ['feedback-triage', 'pia-lessons'];
/** Must match MAX_LESSONS in proxy-server/lib/pia/lessons.js: only the newest travel with a run. */
const ACTIVE_LESSONS = 10;

/**
 * Pia's standing instructions — the "going forward, …" rules owners gave her in report threads.
 * Every one of them travels with every run, so the team can see what shapes her answers and
 * remove a wrong one here. Nothing is added here: a rule is given in a thread, where the reason
 * for it is.
 */
export function PiaLessons() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: QUERY_KEY, queryFn: fetchPiaLessons });
  const remove = useMutation({
    mutationFn: (id: string) => deletePiaLesson(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      showToast({ status: 'success', description: 'Removed — Pia forgets it from her next run on.' });
    },
    onError: (err: Error) => showToast({ status: 'error', description: err.message }),
  });
  const lessons = data?.lessons ?? [];
  if (isLoading) {
    return (
      <section
        aria-label="Pia’s standing instructions"
        aria-busy="true"
        className={`border border-zinc-200 bg-white p-4 ${RADIUS}`}
      >
        <h2 className="text-sm font-semibold text-zinc-900">Pia’s standing instructions</h2>
        <p className="mt-0.5 text-[12.5px] text-zinc-500">Loading…</p>
      </section>
    );
  }
  if (!lessons.length && !error) return null;
  // Lessons come oldest first; the newest ACTIVE_LESSONS are the ones Pia receives.
  const firstActive = Math.max(0, lessons.length - ACTIVE_LESSONS);

  return (
    <section
      aria-label="Pia’s standing instructions"
      className={`border border-zinc-200 bg-white p-4 ${RADIUS}`}
    >
      <h2 className="text-sm font-semibold text-zinc-900">Pia’s standing instructions</h2>
      <p className="mt-0.5 text-[12.5px] text-zinc-500">
        Rules an owner gave Pia in a thread with “going forward” or “in Zukunft”. She follows the
        newest {ACTIVE_LESSONS} on every report; older ones are kept here but no longer sent. Remove
        one and she forgets it from her next run on.
      </p>
      {error ? (
        <p className="mt-2 text-[13px] text-amber-800">{(error as Error).message}</p>
      ) : (
        <ul className="mt-3 divide-y divide-zinc-100">
          {lessons.map((lesson, i) => (
            <li key={lesson.id} className="flex items-start justify-between gap-3 py-2">
              <div className="min-w-0">
                <p className={`text-[13px] ${i >= firstActive ? 'text-zinc-800' : 'text-zinc-400'}`}>
                  “{lesson.text}”
                </p>
                <p className="mt-0.5 text-[12px] text-zinc-500">
                  {i >= firstActive ? '' : 'Not sent any more (older than the newest ten) · '}
                  {lesson.createdAt
                    ? new Date(lesson.createdAt).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })
                    : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => remove.mutate(lesson.id)}
                disabled={remove.isPending}
                className={`inline-flex shrink-0 items-center gap-1 ${RADIUS} border border-zinc-200 px-2 py-1 text-[12px] text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 disabled:opacity-50`}
                aria-label={`Remove: ${lesson.text}`}
              >
                <Trash2 className="size-3" aria-hidden />
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
